import React, { createContext, useCallback, useState, useEffect, useContext, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'devextreme-react/button';
import notificationService from '../services/notificationService';
import dashboardService from '../services/dashboardService';
import { useAuth } from './AuthContext';
import useOrderArrivalAlerts from '../hooks/useOrderArrivalAlerts';
import useNotificationDismissals from '../utils/useNotificationDismissals';
import { getNotificationSummary, NOTICE_PAGE_SIZE } from '../utils/notificationBadges';
import "../styles/Notifications.scss";

export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [toasts, setToasts] = useState([]);
  const [badges, setBadges] = useState({});
  const [orders, setOrders] = useState([]);
  const [notices, setNotices] = useState([]);
  const [ordersError, setOrdersError] = useState(false);
  const [noticesError, setNoticesError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [dismissals, dismissNotifications] = useNotificationDismissals(user);
  const requestVersionRef = useRef(0);
  const inFlightRef = useRef(null);
  const pendingRef = useRef(false);
  const toastSequenceRef = useRef(0);
  const toastTimeoutsRef = useRef(new Map());

  const clearSnapshot = useCallback(() => {
    setBadges({});
    setOrders([]);
    setNotices([]);
    setOrdersError(false);
    setNoticesError(false);
    setLoaded(false);
  }, []);

  // Counts, panel orders and unread notices load together so the bell and panel share one snapshot.
  // A failed part keeps its last successful data; the next poll or reload retries.
  const fetchSnapshot = useCallback(async (version) => {
    const [counts, active, unread] = await Promise.allSettled([
      notificationService.getAll(),
      dashboardService.getActiveOrders(),
      dashboardService.getNotices(1, NOTICE_PAGE_SIZE, { unreadOnly: true }),
    ]);
    if (version !== requestVersionRef.current) return;
    if (counts.status === 'fulfilled') setBadges(counts.value && typeof counts.value === 'object' ? counts.value : {});
    else console.error("NotificationProvider: failed to load badges", counts.reason);
    if (active.status === 'fulfilled') { setOrders(Array.isArray(active.value) ? active.value : []); setOrdersError(false); }
    else setOrdersError(true);
    if (unread.status === 'fulfilled') { setNotices(Array.isArray(unread.value) ? unread.value : []); setNoticesError(false); }
    else setNoticesError(true);
    setLoaded(true);
  }, []);

  // A reload requested while one is running is queued, not dropped, and the returned promise
  // resolves only after that follow-up finishes, so callers see data fetched after their change.
  const loadBadges = useCallback(() => {
    if (!user) {
      requestVersionRef.current += 1;
      inFlightRef.current = null;
      pendingRef.current = false;
      clearSnapshot();
      return Promise.resolve();
    }
    if (inFlightRef.current) {
      pendingRef.current = true;
      return inFlightRef.current;
    }
    const version = requestVersionRef.current;
    let promise;
    const run = async () => {
      try {
        do {
          pendingRef.current = false;
          await fetchSnapshot(version);
        } while (pendingRef.current && version === requestVersionRef.current);
      } finally {
        if (inFlightRef.current === promise) inFlightRef.current = null;
      }
    };
    promise = run();
    inFlightRef.current = promise;
    return promise;
  }, [user, fetchSnapshot, clearSnapshot]);

  useEffect(() => {
    if (!user) {
      clearSnapshot();
      return undefined;
    }
    loadBadges();
    const interval = setInterval(loadBadges, 30000);
    return () => {
      clearInterval(interval);
      requestVersionRef.current += 1;
      inFlightRef.current = null;
      pendingRef.current = false;
    };
  }, [user, loadBadges, clearSnapshot]);

  // Optimistic removal after mark-read/archive; the queued reload then confirms it.
  const removeNotices = useCallback((ids) => {
    setNotices((prev) => (ids === 'all' ? [] : prev.filter((n) => !ids.includes(n.noticeId))));
  }, []);

  const summary = useMemo(
    () => getNotificationSummary({ badges, orders, notices, dismissals, role: user?.role }),
    [badges, orders, notices, dismissals, user?.role]
  );

  // A new order refreshes the bell immediately instead of waiting for the next poll.
  const orderAlerts = useOrderArrivalAlerts(user, loadBadges);

  const dismiss = useCallback(id => {
    const timeout = toastTimeoutsRef.current.get(id);
    if (timeout) clearTimeout(timeout);
    toastTimeoutsRef.current.delete(id);
    setToasts(t => t.filter(x => x.id !== id));
  }, []);

  const notify = useCallback(({ title, message, type='info', duration=5000 }) => {
    const id = `${Date.now()}-${++toastSequenceRef.current}`;
    setToasts(t => [...t, { id, title, message, type }]);
    const timeout = setTimeout(() => {
      toastTimeoutsRef.current.delete(id);
      setToasts(t => t.filter(x => x.id !== id));
    }, duration);
    toastTimeoutsRef.current.set(id, timeout);
  }, []);

  useEffect(() => () => {
    toastTimeoutsRef.current.forEach(clearTimeout);
    toastTimeoutsRef.current.clear();
  }, []);

  return (
    <NotificationContext.Provider value={{
      notify, dismiss, badges, reloadBadges: loadBadges, orderAlerts,
      orders, notices, ordersError, noticesError, loaded,
      dismissals, dismissNotifications, removeNotices, summary,
    }}>
      {children}
      {createPortal(
        <div className="notification-root" aria-live="polite">
          {toasts.map(t => (
            <div key={t.id} className={`toast toast--${t.type}`} onClick={(e) => { if (!e.target.closest?.('.dx-button')) dismiss(t.id); }}>
              <div className="toast-content">
                {t.title && <strong className="toast-title">{t.title}</strong>}
                <div className="toast-message">{t.message}</div>
              </div>
              <Button
                className="app-plain-button toast-close"
                stylingMode="text"
                icon="close"
                hint="Dismiss notification"
                elementAttr={{ 'aria-label': 'Dismiss notification' }}
                onClick={(e) => { e.event?.stopPropagation(); dismiss(t.id); }}
              />
            </div>
          ))}
        </div>,
        document.body
      )}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
